<template>
    <v-container container--fluid>
        <v-layout row wrap align-center justify-space-around class="pa-2 mx-auto">
            <v-flex xs12 class="text-xs-left">
                <h1 class="grey--text lighten-3 mb-2">Profile</h1>
                <p>Use this page to keep your contact and membership details up-to-date.</p>
                <p class="font-italic">All fields marked * are mandatory.</p>
                <v-card class="elevation-12">
                    <v-card-text>
                        <v-form class="overwrite-inputs" ref="updateform" v-model.trim="valid" lazy-validation>
                            <v-container grid-list-lg>
                                <v-layout row wrap class="pa-0">
                                    <v-flex xs12 sm12 md12>
                                        <v-alert :value="true" color="primary lighten-2" icon="info" outlined
                                            >For security reasons you cannot change your username, firstname, lastname, date of birth and gender.<br />However, if you wish to
                                            modify one of those fields, please
                                            <a target="_blank" style="text-decoration: underline;" href="/about/contact-us">contact us</a></v-alert
                                        >
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3">
                                    <v-flex xs12 sm12 md12>
                                        <v-input prepend-icon="card_membership" class="hide-details" hide-details="auto">Membership Details</v-input>
                                    </v-flex>
                                    <v-flex xs12 :class="user.expiry_date ? 'sm4 md4' : 'sm6 md6'">
                                        <v-text-field
                                            class="font-weight-bold grey lighten-4"
                                            prepend-icon="assignment_ind"
                                            label="Member Number"
                                            :value="user.member_number"
                                            hint="Give this number when filling in for a team."
                                            persistent-hint
                                            disabled
                                            readonly
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 :class="user.expiry_date ? 'sm4 md4' : 'sm6 md6'">
                                        <v-text-field
                                            class="font-weight-bold grey lighten-4"
                                            prepend-icon="person"
                                            label="Username"
                                            :value="user.username"
                                            disabled
                                            readonly
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm4 md4 v-if="user.expiry_date">
                                        <v-text-field
                                            class="font-weight-bold grey lighten-4"
                                            prepend-icon="event"
                                            label="Expiry Date"
                                            :value="user.expiry_date"
                                            disabled
                                            readonly
                                        ></v-text-field>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3">
                                    <v-flex xs12 sm12 md12>
                                        <v-input prepend-icon="account_circle" class="hide-details" hide-details="auto">Member Information</v-input>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field prepend-icon="person" label="Legal Firstname*" :value="user.firstname" disabled readonly></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field prepend-icon="person" label="Preferred First Name*" v-model.trim="prefname" :rules="[rules.required]" required :disabled="user.prefname != null"></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field prepend-icon="person" label="Legal Middle Name" v-model.trim="middlename" :disabled="user.middlename != null"></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field prepend-icon="person" label="Legal Lastname*" :value="user.lastname" disabled readonly></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-file-input
                                            class="select-hint"
                                            prepend-icon="image"
                                            show-size
                                            counter
                                            truncate-length="15"
                                            accept=".jpg,.png,.jpeg"
                                            label="Profile Picture"
                                            hint="Please upload a recent photo. Requirements: Portrait. Headshot (shoulders and above), JPG or PNG."
                                            persistent-hint
                                            :rules="[rules.profile]"
                                            v-model.trim="profilephoto"
                                            @change="selectFile"
                                            v-if="can_update_photo"
                                        ></v-file-input>
                                        <p v-if="!can_update_photo"><v-icon medium right>image</v-icon> Profile Picture <span class="caption grey--text text--darken-1">— your picture can only be updated once per registration, after your membership payment</span></p>
                                        <v-avatar class="ml-4" label="Profile" width="auto" height="60" v-if="profilephotosrc">
                                            <img :src="profilephotosrc">
                                        </v-avatar>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field prepend-icon="event" label="Date of Birth*" :value="user.dob" disabled readonly></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-autocomplete
                                            cache-items
                                            :items="countries"
                                            item-text="text"
                                            item-value="value"
                                            label="Country of Birth*"
                                            prepend-icon="public"
                                            hint="Start typing then select a country"
                                            persistent-hint
                                            v-model.trim="countrybirth"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('countrybirth')"
                                            :error-messages="userErrors.get('countrybirth')"
                                            required
                                            @input="setStateBirth(countrybirth)"
                                        ></v-autocomplete>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6 v-if="countrybirth == 'AU'">
                                        <v-select
                                            :items="states"
                                            item-text="text"
                                            item-value="value"
                                            label="State of Birth*"
                                            prepend-icon="location_on"
                                            v-model.trim="statebirth"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('statebirth')"
                                            :error-messages="userErrors.get('statebirth')"
                                            required
                                        ></v-select>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-select
                                            :items="selchoices"
                                            item-text="text"
                                            item-value="value"
                                            label="Do you identify as Aboriginal and/or Torres Strait Islander?*"
                                            prepend-icon="flag"
                                            v-model.trim="aboriginal"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('aboriginal')"
                                            :error-messages="userErrors.get('aboriginal')"
                                            required
                                        ></v-select>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field prepend-icon="perm_identity" label="Gender*" :value="user.gender | capitalize" disabled readonly></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-select
                                            class="select-hint"
                                            :items="identifygenderchoices"
                                            item-text="text"
                                            item-value="value"
                                            label="How do you identify in terms of gender?*"
                                            prepend-icon="perm_identity"
                                            v-model.trim="identifygender"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('identifygender')"
                                            :error-messages="userErrors.get('identifygender')"
                                            required
                                            hint="Please note that our competition categories currently include male and female divisions. If you have any specific needs or considerations related to gender that we should be aware of to ensure your participation is supported, please contact the Association."
                                            persistent-hint
                                        ></v-select>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Email*"
                                            prepend-icon="email"
                                            v-model.trim="email"
                                            :rules="[rules.required, rules.email]"
                                            :error="userErrors.has('email')"
                                            :error-messages="userErrors.get('email')"
                                            required
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Mobile*"
                                            prepend-icon="smartphone"
                                            v-model.trim="mobile"
                                            v-mask="'##########'"
                                            hint="e.g. 0412345678"
                                            persistent-hint
                                            :rules="[rules.required]"
                                            :error="userErrors.has('mobile')"
                                            :error-messages="userErrors.get('mobile')"
                                            maxlength="10"
                                            required
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Home Phone"
                                            prepend-icon="phone"
                                            v-model.trim="phone"
                                            :error="userErrors.has('phone')"
                                            hint="e.g. 0212345678"
                                            persistent-hint
                                            :error-messages="userErrors.get('phone')"
                                            maxlength="10"
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Work Phone"
                                            prepend-icon="work"
                                            v-model.trim="workphone"
                                            :error="userErrors.has('workphone')"
                                            hint="e.g. 0212345678"
                                            persistent-hint
                                            :error-messages="userErrors.get('workphone')"
                                            maxlength="10"
                                        ></v-text-field>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3" v-if="gmap_activated">
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            placeholder="Start typing here to autocomplete your address"
                                            prepend-icon="location_city"
                                            id="member_address"
                                            outlined
                                            dense
                                            filled
                                            hide-details
                                        ></v-text-field>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3">
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Address*"
                                            prepend-icon="location_city"
                                            v-model.trim="address"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('address')"
                                            :error-messages="userErrors.get('address')"
                                            required
                                            id="member_input"
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-autocomplete
                                            :items="localities"
                                            label="Suburb*"
                                            prepend-icon="location_city"
                                            v-model.trim="suburb"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('suburb')"
                                            :error-messages="userErrors.get('suburb')"
                                            required
                                            @input="setLocation(suburb)"
                                        ></v-autocomplete>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Postcode*"
                                            prepend-icon="location_on"
                                            v-model.trim="postcode"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('postcode')"
                                            :error-messages="userErrors.get('postcode')"
                                            required
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-autocomplete
                                            :items="states"
                                            item-text="text"
                                            item-value="value"
                                            label="State*"
                                            prepend-icon="location_on"
                                            v-model.trim="state"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('state')"
                                            :error-messages="userErrors.get('state')"
                                            required
                                        ></v-autocomplete>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3">
                                    <v-flex xs12 sm12 md12>
                                        <v-input prepend-icon="local_hospital" class="input-with-icon" :messages="['Enter medical/allergy information']">Medical Information</v-input>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-select
                                            :items="selchoices"
                                            item-text="text"
                                            item-value="value"
                                            label="Do you have a physical, intellectual or a non-visible (hidden) disability?*"
                                            prepend-icon="accessible"
                                            v-model.trim="disability"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('disability')"
                                            :error-messages="userErrors.get('disability')"
                                            required
                                            @input="setDisabilityType(disability)"
                                        ></v-select>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12 v-if="disability == 'yes'">
                                        <v-select
                                            :items="disabilitytypechoices"
                                            item-text="text"
                                            item-value="value"
                                            label="Please indicate type of impairment."
                                            prepend-icon="accessible"
                                            v-model.trim="disabilitytype"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('disabilitytype')"
                                            :error-messages="userErrors.get('disabilitytype')"
                                            required
                                        ></v-select>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-textarea
                                            outlined
                                            prepend-icon="local_hospital"
                                            v-model.trim="ec_medical"
                                            auto-grow
                                            label="Medical Condition(s)"
                                            :error="userErrors.has('ec_medical')"
                                            :error-messages="userErrors.get('ec_medical')"
                                        ></v-textarea>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3">
                                    <v-flex xs12 sm12 md12>
                                        <v-input prepend-icon="contact_phone" class="input-with-icon" :messages="['Enter emergency contact(s) information']">Emergency Contact(s)</v-input>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Contact Fullname*"
                                            prepend-icon="person"
                                            v-model.trim="ec_fullname"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('ec_fullname')"
                                            :error-messages="userErrors.get('ec_fullname')"
                                            required
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Contact Mobile*"
                                            prepend-icon="phone"
                                            v-model.trim="ec_mobile"
                                            v-mask="'##########'"
                                            hint="e.g. 0412345678"
                                            persistent-hint
                                            :rules="[rules.required]"
                                            :error="userErrors.has('ec_mobile')"
                                            :error-messages="userErrors.get('ec_mobile')"
                                            maxlength="10"
                                            required
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Contact 2 Fullname"
                                            prepend-icon="person"
                                            v-model.trim="ec_fullname_2"
                                            :error="userErrors.has('ec_fullname_2')"
                                            :error-messages="userErrors.get('ec_fullname_2')"
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Contact 2 Mobile"
                                            prepend-icon="phone"
                                            v-model.trim="ec_mobile_2"
                                            v-mask="'##########'"
                                            hint="e.g. 0412345678"
                                            persistent-hint
                                            :error="userErrors.has('ec_mobile_2')"
                                            :error-messages="userErrors.get('ec_mobile_2')"
                                            maxlength="10"
                                        ></v-text-field>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap class="px-0 py-3">
                                    <v-flex xs12 sm12 md12>
                                        <v-input prepend-icon="info" class="input-with-icon" :messages="['Please enter additional information']">Other Information</v-input>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-select
                                            :items="jobs"
                                            item-text="text"
                                            item-value="value"
                                            label="Industry "
                                            prepend-icon="business_center"
                                            v-model.trim="job"
                                            :error="userErrors.has('job')"
                                            :error-messages="userErrors.get('job')"
                                        ></v-select>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Job Title"
                                            prepend-icon="keyboard"
                                            v-model.trim="job_title"
                                            :error="userErrors.has('job_title')"
                                            :error-messages="userErrors.get('job_title')"
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="School / University"
                                            prepend-icon="school"
                                            v-model.trim="school"
                                            :error="userErrors.has('school')"
                                            :error-messages="userErrors.get('school')"
                                        ></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field
                                            label="Skills"
                                            prepend-icon="extension"
                                            v-model.trim="skills"
                                            :error="userErrors.has('skills')"
                                            :error-messages="userErrors.get('skills')"
                                        ></v-text-field>
                                    </v-flex>
                                    <!-- <v-flex xs12 sm6 md6>
                                        <v-text-field label="Height" v-model.trim="height" :error="userErrors.has('height')" :error-messages="userErrors.get('height')" suffix="cm"></v-text-field>
                                    </v-flex>
                                    <v-flex xs12 sm6 md6>
                                        <v-text-field label="Weight" v-model.trim="weight" :error="userErrors.has('weight')" :error-messages="userErrors.get('weight')" suffix="kgs"></v-text-field>
                                    </v-flex> -->
                                </v-layout>
                                <v-layout row wrap class="pa-0 py-3">
                                    <v-flex xs12 sm12 md12>
                                        <v-input prepend-icon="gavel" class="hide-details" hide-details="auto">Terms & Conditions</v-input>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12 v-if="isAdult">
                                        <v-expansion-panels dark :value="0">
                                            <v-expansion-panel class="primary white--text">
                                                <v-expansion-panel-header
                                                    ><v-icon left style="line-height: 18px;flex: initial;">error</v-icon>Terms & Conditions</v-expansion-panel-header
                                                >
                                                <v-expansion-panel-content class="primary white--text">
                                                    <v-card light>
                                                        <v-card-text>
                                                            <v-alert :value="true" color="primary lighten-2" icon="priority_high" outlined>
                                                                <p>
                                                                    By ticking this box, you declare that you have read, understood, acknowledge and agree to the
                                                                    <a
                                                                        href="https://australia.basketball/wp-content/uploads/2019/09/The-Basketball-Network-Participant-Terms-and-Conditions.pdf"
                                                                        target="_blank"
                                                                        >{{$route.query.sport}} Network Participant Terms and Conditions</a
                                                                    >
                                                                    and
                                                                    <a href="https://australia.basketball/integrity/" target="_blank"
                                                                        >{{$route.query.sport}} Australia National Integrity Framework</a
                                                                    >, including the exclusion of implied terms, warning, assumption of risk, release and indemnity. In addition,
                                                                    you agree that you will be bound by {{ appNameUrls.assoc_name }} constitution and any regulations and policies
                                                                    (including competition rules or by-laws) made under it.
                                                                    <a href="/about/policy-procedures/" target="_blank">Click here</a> to view {{ appNameUrls.assoc_name }}
                                                                    Competition By-Laws, policies and procedures.
                                                                </p>
                                                            </v-alert>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12 v-if="!isAdult">
                                        <v-expansion-panels dark :value="0">
                                            <v-expansion-panel class="primary white--text">
                                                <v-expansion-panel-header
                                                    ><v-icon left style="line-height: 18px;flex: initial;">error</v-icon>Where the applicant is under 18 years of age the below box
                                                    must be ticked by the applicant’s parent or legal guardian</v-expansion-panel-header
                                                >
                                                <v-expansion-panel-content class="primary white--text">
                                                    <v-card light>
                                                        <v-card-text>
                                                            <v-alert :value="true" color="info" icon="priority_high" outlined>
                                                                <p>
                                                                    You are the parent or guardian of the applicant. By ticking this box you authorise and consent to the applicant
                                                                    registering as a Participant and member and participating in the {{$route.query.sport}} Activities. In consideration of the
                                                                    Participant's registration being accepted, you expressly agree to be responsible for the Participant's behaviour
                                                                    and agree to accept in your capacity as parent or guardian the
                                                                    <a
                                                                        href="https://australia.basketball/wp-content/uploads/2019/09/The-Basketball-Network-Participant-Terms-and-Conditions.pdf"
                                                                        target="_blank"
                                                                        >{{$route.query.sport}} Network Participant Terms and Conditions</a
                                                                    >
                                                                    and
                                                                    <a href="https://australia.basketball/integrity/" target="_blank"
                                                                        >{{$route.query.sport}} Australia National Integrity Framework</a
                                                                    >, including the exclusion of implied terms, warning, assumption of risk, release and indemnity. In addition,
                                                                    you agree that you and your son/daughter will be bound by {{ appNameUrls.assoc_name }} constitution and any
                                                                    regulations and policies (including competition rules or by-laws) made under it.
                                                                    <a href="/about/policy-procedures/" target="_blank">Click here</a> to view {{ appNameUrls.assoc_name }}
                                                                    Competition By-Laws, policies and procedures.
                                                                </p>
                                                            </v-alert>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </v-flex>
                                    <!-- <v-flex xs12 sm12 md12 v-if="!isAdult">
                                        <v-checkbox  class="mt-1 d-inline-block" v-model.trim="agreed_to_indemnity" :rules="[rules.required]" :error="userErrors.has('agreed_to_indemnity')" :error-messages="userErrors.get('agreed_to_indemnity')" required>
                                            <template v-slot:label>
                                                <div>I agree to</div>
                                            </template>
                                        </v-checkbox> <a class="link-policy" target="_blank" href="/about/policy-procedures/">Applicant’s parent or legal guardian Terms & Conditions*</a>
                                    </v-flex> -->
                                    <v-flex xs12 sm12 md12>
                                        <v-checkbox
                                            class="mt-1 d-inline-block"
                                            v-model.trim="agreed_to_terms"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('agreed_to_terms')"
                                            :error-messages="userErrors.get('agreed_to_terms')"
                                            required
                                        >
                                            <template v-slot:label>
                                                <div>I agree <span v-cloak v-if="!isAdult">on behalf of the applicant</span>&nbsp;to&nbsp;</div>
                                                <a class="link-policy" target="_blank" href="/about/policy-procedures/">Registration Terms & Conditions*</a>
                                            </template>
                                        </v-checkbox>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-checkbox
                                            class="mt-1 d-inline-block"
                                            v-model.trim="agreed_to_privacy"
                                            :rules="[rules.required]"
                                            :error="userErrors.has('agreed_to_privacy')"
                                            :error-messages="userErrors.get('agreed_to_privacy')"
                                            required
                                        >
                                            <template v-slot:label>
                                                <div>I agree <span v-cloak v-if="!isAdult">on behalf of the applicant</span>&nbsp;to&nbsp;</div>
                                                <a class="link-policy" target="_blank" href="/about/policy-procedures/">{{ appNameUrls.assoc_name }} Policy and Procedures, Competition and Tribunal By-Laws*</a>
                                            </template>
                                        </v-checkbox>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-checkbox
                                            class="mt-1"
                                            v-model.trim="subscribed_to_newsletter"
                                            :error="userErrors.has('subscribed_to_newsletter')"
                                            :error-messages="userErrors.get('subscribed_to_newsletter')"
                                        >
                                            <template v-slot:label>
                                                <div>Subscribe to Newsletter</div>
                                            </template>
                                        </v-checkbox>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-checkbox
                                            class="mt-1"
                                            v-model.trim="subscribed_to_sms"
                                            :error="userErrors.has('subscribed_to_sms')"
                                            :error-messages="userErrors.get('subscribed_to_sms')"
                                        >
                                            <template v-slot:label>
                                                <div>Subscribe to Sms Communication</div>
                                            </template>
                                        </v-checkbox>
                                    </v-flex>
                                    <v-flex xs12 sm12 md12>
                                        <v-checkbox
                                            class="mt-1"
                                            v-model.trim="subscribed_to_comm"
                                            :error="userErrors.has('subscribed_to_comm')"
                                            :error-messages="userErrors.get('subscribed_to_comm')"
                                        >
                                            <template v-slot:label>
                                                <div>Subscribe to Email Communication</div>
                                            </template>
                                        </v-checkbox>
                                    </v-flex>
                                </v-layout>
                            </v-container>
                        </v-form>
                        <v-alert v-model.trim="alert" outlined transition="scale-transition" dismissible type="error">Error! Please check the form above for errors</v-alert>
                    </v-card-text>
                    <v-divider light></v-divider>
                    <v-card-actions class="py-4 justify-end">
                        <v-btn text @click="cancel" :disabled="userLoading" color="error">cancel</v-btn>
                        <v-btn :loading="userLoading" :disabled="!valid" color="primary" @click="update">update<v-icon right>edit</v-icon></v-btn>
                    </v-card-actions>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import store from "@store";
import moment from "moment";

window.checkAndAttachMapScript = function (callback) {
   let scriptId = "map-api-script";
   let mapAlreadyAttached = !!document.getElementById(scriptId);

    if (mapAlreadyAttached) {
        if (window.google){ // Script attached but may not finished loading; so check for 'google' object.
            callback();
            }
    } else {
        window.mapApiInitialized = callback;
        let api_key = window.LMP.lmpGmap.apikey;

        let script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://maps.googleapis.com/maps/api/js?key='+api_key+'&libraries=places&callback=mapApiInitialized&loading=async';
        document.body.appendChild(script);
    }

    return mapAlreadyAttached;
}

export default {
    name: "ProfileModule",
    components: {
        //
    },
    data() {
        return {
            rules: {
                required: v => !!v || "This field is required",
                profile: v => !v || v.size < 500000 || 'Profile picture size should be less than 500 KB!',
                email: v => {
                    if (v) {
                        return /.+@.+/.test(v) || "Please enter a valid email";
                    }

                    return true;
                },
                //email: value => {
                //    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                //    return pattern.test(value) || 'Invalid e-mail.'
                //  }
            },
            email: "",
            mobile: "",
            address: "",
            suburb: "",
            postcode: "",
            state: "",
            phone: "",
            workphone: "",
            job: "",
            height: "",
            weight: "",
            ec_fullname: "",
            ec_mobile: "",
            ec_fullname_2: "",
            ec_mobile_2: "",
            ec_medical: "",
            skills: "",
            job_title: "",
            school: "",
            agreed_to_terms: false,
            agreed_to_privacy: false,
            agreed_to_indemnity: false,
            subscribed_to_newsletter: false,
            subscribed_to_sms: false,
            subscribed_to_comm: true,
            valid: true,
            alert: false,

            selchoices: [{ text: "Yes", value: "yes" }, { text: "No", value: "no" }, { text: "Prefer not to say", value: "notsay" }],
            identifygenderchoices: [{ text: "Male", value: "male" }, { text: "Female", value: "female" }, { text: "Non-binary", value: "non-binary" }],
            disabilitytypechoices: [{ text: "Physical Impairment", value: "physical" }, { text: "Intellectual / Learning Impairment", value: "intellectual" }, { text: "Neurological Impairment", value: "neurological" }, { text: "Sensory and/or Speech Impairment", value: "sensory" }, { text: "Other", value: "other" }],
            disability:'',
            disabilitytype: '',
            identifygender: '',
            countrybirth: '',
            statebirth: '',
            aboriginal: '',
            profilephoto: null,
            profilephotosrc: '',
            can_update_photo: false,
            memb_renewal: false,
            middlename: '',
            prefname: '',

            autocomplete: null,
            gmap_activated: window.LMP.lmpGmap.activated
        };
    },
    computed: {
        userErrors() {
            return this.$store.getters["user/userErrors"];
        },
        userLoading() {
            return this.$store.getters["user/userStatus"] == "loading";
        },
        user() {
            const u = this.$store.getters["user/getProfile"];

            this.email = u.email;
            this.mobile = u.mobile;
            this.address = u.address;
            this.suburb = u.suburb;
            this.postcode = u.postcode;
            this.state = u.state;
            this.phone = u.phone;
            this.workphone = u.workphone;
            this.job = u.job;
            this.height = u.height;
            this.weight = u.weight;
            this.ec_fullname = u.ec_fullname;
            this.ec_mobile = u.ec_mobile;
            this.ec_fullname_2 = u.ec_fullname_2;
            this.ec_mobile_2 = u.ec_mobile_2;
            this.ec_medical = u.ec_medical;
            this.skills = u.skills;
            this.job_title = u.job_title;
            this.school = u.school;
            this.agreed_to_terms = u.agreed_to_terms;
            this.agreed_to_privacy = u.agreed_to_privacy;
            this.agreed_to_indemnity = u.agreed_to_indemnity;
            this.subscribed_to_newsletter = u.subscribed_to_newsletter;
            this.subscribed_to_sms = u.subscribed_to_sms;
            this.subscribed_to_comm = u.subscribed_to_comm;

            this.middlename = u.middlename;
            this.prefname = u.prefname;
            this.disability = u.disability;
            this.disabilitytype = u.disabilitytype;
            this.identifygender = u.identifygender;
            this.countrybirth = u.countrybirth;
            this.statebirth = u.statebirth;
            this.aboriginal = u.aboriginal;
            this.profilephotosrc = u.profilephotosrc;
            this.can_update_photo = u.can_update_photo;

            this.memb_renewal = u.memb_renewal;

            return u;
        },
        isAdult() {
            return moment(this.user["dob"], "DD-MM-YYYY").isBefore(moment().subtract(18, "years"));
        },
        states() {
            return this.$store.getters["common/getData"]("states");
        },
        countries() {
            return this.$store.getters["common/getData"]("countries");
        },
        jobs() {
            return this.$store.getters["common/getData"]("jobs");
        },
        localities() {
            return this.$store.getters["common/getData"]("localities");
        },
    },
    methods: {
        updateProfileAddress(){
            let place = this.autocomplete.getPlace();

            if (place) {
                let address1 = place.formatted_address.split(', ');
                this.address = address1[0];

                for (const component of place.address_components) {
                    const componentType = component.types[0];

                    switch (componentType) {
                        case "postal_code": {
                            this.postcode = component.long_name;
                            break;
                        }
                        case "locality": {
                            this.suburb = component.long_name.toUpperCase();
                            break;
                        }
                        case "administrative_area_level_1": {
                            this.state = component.short_name;
                            break;
                        }
                    }
                }

                setTimeout(() => {
                    document.getElementById('member_address').value = '';
                }, 100);
                setTimeout(() => {
                    document.getElementById('member_address').value = '';
                }, 100);

                document.getElementById('member_input').focus();
            }
        },
        initLocationSearch() {
            this.autocomplete = new window.google.maps.places.Autocomplete(
                document.getElementById('member_address'), {
                    componentRestrictions: { country: ["au"] },
                    fields: ["address_components", "formatted_address"],
                    types: ["address"],
                }
            );

            this.autocomplete.addListener("place_changed", this.updateProfileAddress);
        },
        selectFile(e){
            this.profilephoto = e.target.files[0];
        },
        needRequiredFields() {
            if (
                !(
                    this.firstname &&
                    this.lastname &&
                    this.dob &&
                    this.gender &&
                    this.address &&
                    this.suburb &&
                    this.state &&
                    this.postcode &&
                    this.mobile &&
                    this.phone &&
                    this.email &&
                    this.ec_fullname &&
                    this.ec_mobile &&
                    this.agreed_to_terms &&
                    this.agreed_to_privacy &&

                    this.prefname &&
                    this.identifygender &&
                    this.countrybirth &&
                    this.aboriginal &&
                    this.disability

                )
            ) {
                this.$refs.updateform.validate();
            }
        },
        setLocation(suburb) {
            if (suburb) {
                let locality = [];

                locality = this.localities.filter(function(object) {
                    return object.value == suburb;
                })[0];

                this.postcode = locality.postcode;
                this.state = locality.state;
            }
        },
        setStateBirth(country) {
            if (country != 'AU') {
                this.statebirth = '';
            }
        },
        setDisabilityType(disability) {
            if (disability != 'yes') {
                this.disabilitytype = '';
            }
        },
        cancel() {
            this.$router.push({ name: "dashboard" });
        },
        update() {
            if (this.$refs.updateform.validate()) {
                this.valid = false;
                this.agreed_to_indemnity = this.agreed_to_privacy;

                const {
                    email,
                    mobile,
                    address,
                    suburb,
                    postcode,
                    state,
                    phone,
                    workphone,
                    job,
                    height,
                    weight,
                    ec_fullname,
                    ec_mobile,
                    ec_fullname_2,
                    ec_mobile_2,
                    ec_medical,
                    skills,
                    school,
                    job_title,
                    agreed_to_terms,
                    agreed_to_privacy,
                    agreed_to_indemnity,
                    subscribed_to_newsletter,
                    subscribed_to_sms,
                    subscribed_to_comm,

                    middlename,
                    prefname,
                    disability,
                    disabilitytype,
                    identifygender,
                    countrybirth,
                    statebirth,
                    aboriginal,
                    profilephoto,
                    profilephotosrc
                } = this;

                this.$store
                    .dispatch("user/userUpdate", {
                        email,
                        mobile,
                        address,
                        suburb,
                        postcode,
                        state,
                        phone,
                        workphone,
                        job,
                        height,
                        weight,
                        ec_fullname,
                        ec_mobile,
                        ec_fullname_2,
                        ec_mobile_2,
                        ec_medical,
                        skills,
                        school,
                        job_title,
                        agreed_to_terms,
                        agreed_to_privacy,
                        agreed_to_indemnity,
                        subscribed_to_newsletter,
                        subscribed_to_sms,
                        subscribed_to_comm,
                        sport: this.$route.query.sport.toLowerCase().trim(),

                        middlename,
                        prefname,
                        disability,
                        disabilitytype,
                        identifygender,
                        countrybirth,
                        statebirth,
                        aboriginal,
                        profilephoto,
                        profilephotosrc
                    })
                    .then(() => {
                        this.$router.push({ name: "dashboard" });
                    })
                    .catch(err => {
                        this.valid = true;
                        this.alert = true;
                        // console.log(err);
                    });
            } else {
                this.valid = true;
                this.alert = true;
            }
        },
    },
    mounted() {
        this.needRequiredFields();

        if(window.LMP.lmpGmap.activated){
            window.checkAndAttachMapScript(this.initLocationSearch);
        }
    },
    beforeCreate() {
        if (_.isEmpty(this.$store.getters["common/getData"]("localities"))) {
            this.$store.dispatch("common/dataRequest", { type: "localities" });
        }

        if (_.isEmpty(this.$store.getters["common/getData"]("states"))) {
            this.$store.dispatch("common/dataRequest", { type: "states" });
        }

        if (_.isEmpty(this.$store.getters["common/getData"]("countries"))) {
            this.$store.dispatch("common/dataRequest", { type: "countries" });
        }

        if (_.isEmpty(this.$store.getters["common/getData"]("jobs"))) {
            this.$store.dispatch("common/dataRequest", { type: "jobs" });
        }
    },
};
</script>

<style lang="scss" scoped>
[v-cloak] {
    display: none;
}
[v-cloak] > * {
    display: none;
}

.v-alert .v-alert__icon.v-icon {
    align-self: flex-start;
}

.link-policy {
    vertical-align: super;
    font-weight: normal;
    font-family: Roboto, sans-serif;
    font-size: 16px;
}

::v-deep .v-input.input-with-icon .v-input__prepend-outer {
    margin-top: 0;
}

::v-deep .v-input.input-with-icon .v-input__control .v-input__slot {
    // margin-bottom: 0;
    // margin-top: 2px;
}

::v-deep .v-input.hide-details.v-input--hide-details {
    align-items: center;
}

::v-deep .select-hint .v-text-field__details .v-messages__message {
    font-size: 10px !important;
    color: #d3473c !important;
}
</style>
